<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-sm-12">
        <div class="card">
          <div class="card-header">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <h4>KYTs by Log Number</h4>
                <span v-if="kytData">Parent Log: <strong>{{ kytData.parentMainLogNo }}</strong></span>
              </div>
              <button 
                class="btn btn-primary btn-sm" 
                @click="refreshData"
                :disabled="loading"
              >
                <i class="fa fa-refresh" :class="{ 'fa-spin': loading }"></i>
                {{ loading ? 'Loading...' : 'Refresh' }}
              </button>
            </div>
          </div>
          
          <div class="card-body">
            <!-- Loading state -->
            <div v-if="loading" class="text-center p-4">
              <div class="spinner-border text-primary" role="status">
                <span class="sr-only">Loading...</span>
              </div>
              <p class="mt-2">Loading KYTs...</p>
            </div>

            <!-- Error state -->
            <div v-if="error" class="alert alert-danger" role="alert">
              <i class="fa fa-exclamation-triangle"></i>
              {{ error }}
            </div>

            <!-- Search and Filters -->
            <div v-if="kytData && !loading" class="row mb-4">
              <div class="col-md-6">
                <div class="form-group">
                  <label for="search">Search KYTs</label>
                  <input 
                    id="search"
                    type="text" 
                    class="form-control" 
                    placeholder="Search by site name, employee, or category..."
                    v-model="searchTerm"
                  >
                </div>
              </div>
              <div class="col-md-3">
                <div class="form-group">
                  <label for="statusFilter">Filter by Status</label>
                  <select 
                    id="statusFilter"
                    class="form-control" 
                    v-model="statusFilter"
                  >
                    <option value="">All Status</option>
                    <option value="completed">Completed</option>
                    <option value="approved">Approved</option>
                    <option value="pending">Pending</option>
                    <option value="open">Open</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
              </div>
              <div class="col-md-3">
                <div class="form-group">
                  <label for="priorityFilter">Filter by Priority</label>
                  <select 
                    id="priorityFilter"
                    class="form-control" 
                    v-model="priorityFilter"
                  >
                    <option value="">All Priority</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>
              </div>
            </div>

            <!-- KYTs Content -->
            <div v-if="kytData && !loading" class="kyts-container">
              <!-- Parent KYT Photos Gallery -->
              <div v-if="kytData.parentKyt && kytData.parentKyt.photos && kytData.parentKyt.photos.length > 0" class="parent-photos-section mb-4">
                <div class="card">
                 
                  <div class="card-body">
                    <div class="parent-photos-gallery">
                      <div 
                        v-for="(photo, index) in kytData.parentKyt.photos" 
                        :key="index"
                        class="parent-photo-item"
                        @click="openImageModal(photo, `Parent KYT Photo ${index + 1}`)"
                      >
                        <img 
                          :src="photo" 
                          :alt="`Parent KYT photo ${index + 1}`"
                          class="parent-photo-thumbnail"
                        />
                        <div class="parent-photo-overlay">
                          <i class="fa fa-search-plus"></i>
                        </div>
                        <div class="parent-photo-number">{{ index + 1 }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Summary -->
              <div class="row mb-4">
                <div class="col-md-4">
                  <div class="card bg-primary text-white">
                    <div class="card-body text-center">
                      <h5 class="card-title">{{  kytData.totalChildKyts }}</h5>
                      <p class="card-text">Total</p>
                      <!-- <small>({{ }} KYTs)</small> -->
                    </div>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="card bg-success text-white">
                    <div class="card-body text-center">
                      <h5 class="card-title">{{ getCompletedKyts() }}</h5>
                      <p class="card-text">Completed</p>
                    </div>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="card bg-warning text-white">
                    <div class="card-body text-center">
                      <h5 class="card-title">{{ getPendingKyts() }}</h5>
                      <p class="card-text">Pending</p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- KYTs Table -->
              <div class="table-responsive">
                <table class="table table-hover table-striped">
                  <thead class="thead-dark">
                    <tr>
                      <th width="10%">Employee</th>
                      <th width="8%">Site UID</th>
                      <th width="6%">Previous NFD</th>
                      <th width="6%">Visited</th>
                      <th width="6%">Future NFD</th>
                      <th width="8%">Status</th>
                      <th width="16%">Category, Problem & Solution</th>
                      <th width="10%">Assignments</th>
                      <th width="18%">Penalties & Warnings</th>
                      <th width="12%">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-for="kyt in sortedKyts" :key="kyt._id">
                      <template v-for="(category, catIndex) in kyt.categoryData" :key="`${kyt._id}-${catIndex}`">
                        <tr class="kyt-row">
                          <!-- Employee (only show on first category row) -->
                          <td v-if="catIndex === 0" :rowspan="kyt.categoryData.length" class="align-middle">
                            <div class="employee-info">
                              <strong>{{ kyt.employee.employeeName }}</strong>
                              <br>
                              <small class="text-muted">{{ kyt.employee.employeeCode }}</small>
                            </div>
                          </td>
                          
                          <!-- Site UID (only show on first category row) -->
                          <td v-if="catIndex === 0" :rowspan="kyt.categoryData.length" class="align-middle">
                            <span class="badge badge-info">{{ kyt.site.siteUID }}</span>
                          </td>
                          
                          <!-- Previous NFD (only show on first category row) -->
                          <td v-if="catIndex === 0" :rowspan="kyt.categoryData.length" class="align-middle">
                            <small>{{ kyt.previousNfd ? formatDate(kyt.previousNfd) : 'N/A' }}</small>
                          </td>
                          
                          <!-- Created Date/Visited (only show on first category row) -->
                          <td v-if="catIndex === 0" :rowspan="kyt.categoryData.length" class="align-middle">
                            <small>{{ formatDate(kyt.createdAt) }}</small>
                          </td>
                          
                          <!-- Future NFD (only show on first category row) -->
                          <td v-if="catIndex === 0" :rowspan="kyt.categoryData.length" class="align-middle">
                            <small>{{ formatDate(kyt.nfd) }}</small>
                          </td>
                          
                          <!-- Status (show KYT status) -->
                          <td>
                            <div class="status-info">
                              <span 
                                class="badge mb-1"
                                :class="getStatusBadgeClass(kyt.kytStatus)"
                              >
                                {{ getDisplayStatus(kyt.kytStatus) || 'N/A' }}
                              </span>
                            
                            </div>
                          </td>
                          
                          <!-- Category & Problem -->
                          <td>
                            <div class="category-info">
                              <div class="mb-1">
                                <span class="badge badge-secondary badge-sm">{{ category.category?.toUpperCase() || 'N/A' }}</span><br>
                                <strong class="ml-2">{{ getSubCategoryName(category, kyt) || 'N/A' }}</strong>
                              </div>
                              <div class="mb-2">
                                <div class="mb-1">
                                  <strong class="small text-danger">Problem:</strong>
                                  <p class="mb-0 text-muted small">{{ category.problem || 'No problem description' }}</p>
                                </div>
                                <div class="mb-1">
                                  <strong class="small text-success">Solution:</strong>
                                  <p class="mb-0 text-muted small">{{ category.solution || 'No solution provided' }}</p>
                                </div>
                              </div>
                              <div class="mt-1">
                                <small class="text-info">Log: {{ category.logNo || 'N/A' }}</small>
                              </div>
                            </div>
                          </td>
                          
                          <!-- Assignments -->
                          <td>
                            <div class="assignments-compact">
                              <div 
                                v-for="assignment in category.employeeAssignments" 
                                :key="assignment._id"
                                class="assignment-row mb-1"
                              >
                                <div class="d-flex justify-content-between align-items-center">
                                  <div class="assignment-employee">
                                    <small>
                                      <strong>{{ assignment.employee.employeeName }}</strong>
                                      <span class="text-muted">({{ assignment.employee.employeeCode }})</span>
                                    </small>
                                  </div>
                                  <div class="assignment-badges">
                                    <span 
                                      class="badge badge-xs mr-1"
                                      :class="getPriorityBadgeClass(assignment.priority)"
                                    >
                                      {{ assignment.priority?.toUpperCase() || 'N/A' }}
                                    </span>
                                    <span 
                                      class="badge badge-xs mr-1"
                                      :class="assignment.isCompletedWork ? 'badge-success' : 'badge-warning'"
                                    >
                                      {{ assignment.isCompletedWork ? 'DONE' : 'PENDING' }}
                                    </span>
                                    <span 
                                      v-if="assignment.workStarted"
                                      class="badge badge-xs badge-info"
                                    >
                                      STARTED
                                    </span>
                                    <span 
                                      v-if="assignment.isVerified"
                                      class="badge badge-xs badge-primary"
                                    >
                                      VERIFIED
                                    </span>
                                    <span 
                                      v-if="assignment.requiresRework"
                                      class="badge badge-xs badge-danger"
                                    >
                                      REWORK
                                    </span>
                                  </div>
                                </div>
                                <div v-if="assignment.isCompletedWork && assignment.workCompletedAt" class="completion-time mt-1">
                                  <small class="text-success">
                                    <i class="fa fa-check-circle"></i>
                                    Completed: {{ formatDateTime(assignment.workCompletedAt) }}
                                  </small>
                                </div>
                                <div v-if="assignment.workNotes" class="work-notes mt-1">
                                  <small class="text-info">
                                    <i class="fa fa-sticky-note"></i>
                                    Notes: {{ assignment.workNotes }}
                                  </small>
                                </div>
                                <div v-if="assignment.photos && assignment.photos.length > 0" class="assignment-photos mt-1">
                                  <div class="photos-header mb-1">
                                    <small class="text-primary">
                                      <i class="fa fa-camera"></i>
                                      Photos ({{ assignment.photos.length }}):
                                    </small>
                                  </div>
                                  <div class="photos-gallery d-flex flex-wrap">
                                    <div 
                                      v-for="(photo, photoIndex) in assignment.photos" 
                                      :key="photoIndex"
                                      class="photo-thumbnail mr-1 mb-1"
                                    >
                                      <img 
                                        :src="photo" 
                                        :alt="`Assignment photo ${photoIndex + 1}`"
                                        class="img-thumbnail"
                                        @click="openImageModal(photo, `Assignment ${assignment.assignmentId} - Photo ${photoIndex + 1}`)"
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                          
                          <!-- Penalties & Warnings Overview -->
                          <td>
                            <div class="penalties-overview">
                              <div v-if="category.assignedPenaltiesWarnings && category.assignedPenaltiesWarnings.length > 0">
                                <!-- Summary badges -->
                                <div class="penalties-summary mb-2">
                                  <span class="badge badge-danger badge-sm mr-1">
                                    {{ category.assignedPenaltiesWarnings.length }} Actions
                                  </span>
                                  <span 
                                    v-if="getTotalPenaltyAmount(category.assignedPenaltiesWarnings) > 0"
                                    class="badge badge-warning badge-sm"
                                  >
                                    ₹{{ getTotalPenaltyAmount(category.assignedPenaltiesWarnings) }}
                                  </span>
                                </div>
                                
                                <!-- Brief list of actions -->
                                <div class="penalties-brief">
                                  <div 
                                    v-for="penalty in category.assignedPenaltiesWarnings.slice(0, 2)" 
                                    :key="penalty.employeeId + penalty.actionDate"
                                    class="penalty-brief-item"
                                  >
                                    <small class="d-flex justify-content-between align-items-center">
                                      <span>
                                        <strong>{{ penalty.employeeName }}</strong>
                                      </span>
                                      <span 
                                        class="badge badge-xs"
                                        :class="getActionBadgeClass(penalty.action)"
                                      >
                                        {{ penalty.action?.toUpperCase() }}
                                      </span>
                                    </small>
                                  </div>
                                  <div v-if="category.assignedPenaltiesWarnings.length > 2" class="text-center mt-1">
                                    <small class="text-muted">
                                      +{{ category.assignedPenaltiesWarnings.length - 2 }} more...
                                    </small>
                                  </div>
                                </div>
                              </div>
                              <div v-else class="text-center text-muted">
                                <small>
                                  <i class="fa fa-check-circle text-success"></i>
                                  No penalties
                                </small>
                              </div>
                            </div>
                          </td>

                          <!-- Actions -->
                          <td>
                            <div class="text-center">
                              <button 
                                v-if="category.assignedPenaltiesWarnings && category.assignedPenaltiesWarnings.length > 0"
                                class="btn btn-sm btn-outline-primary" 
                                @click="openPenaltiesModal(category)"
                                :title="`View detailed penalties with images`"
                              >
                                <i class="fa fa-eye"></i>
                                View Details
                              </button>
                              <span v-else class="text-muted">
                                <small>No actions</small>
                              </span>
                            </div>
                          </td>
                        </tr>
                      </template>
                    </template>
                  </tbody>
                </table>
              </div>

              <!-- Pagination (if needed for future use) -->
              <div v-if="kytData.pagination.totalPages > 1" class="pagination-wrapper mt-4">
                <nav>
                  <ul class="pagination justify-content-center">
                    <li class="page-item" :class="{ disabled: !kytData.pagination.hasPrev }">
                      <a class="page-link" href="#" @click.prevent="changePage(kytData.pagination.currentPage - 1)">Previous</a>
                    </li>
                    <li class="page-item active">
                      <a class="page-link" href="#">{{ kytData.pagination.currentPage }}</a>
                    </li>
                    <li class="page-item" :class="{ disabled: !kytData.pagination.hasNext }">
                      <a class="page-link" href="#" @click.prevent="changePage(kytData.pagination.currentPage + 1)">Next</a>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>

            <!-- Empty state -->
            <div v-if="kytData && kytData.totalChildKyts === 0" class="text-center p-5">
              <i class="fa fa-folder-open fa-3x text-muted mb-3"></i>
              <h5 class="text-muted">No KYTs Found</h5>
              <p class="text-muted">No child KYTs found for this log number.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Image Modal -->
    <div 
      v-if="selectedImage"
      class="image-modal-overlay" 
      @click="closeImageModal"
    >
      <div class="image-modal-content" @click.stop>
        <div class="image-modal-header">
          <h5 class="modal-title">{{ selectedImageTitle }}</h5>
          <button 
            type="button" 
            class="close" 
            @click="closeImageModal"
            aria-label="Close"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="image-modal-body">
          <img 
            :src="selectedImage" 
            :alt="selectedImageTitle"
            class="modal-image"
          />
        </div>
        <div class="image-modal-footer">
          <button 
            type="button" 
            class="btn btn-secondary" 
            @click="closeImageModal"
          >
            Close
          </button>
          <a 
            :href="selectedImage" 
            target="_blank" 
            class="btn btn-primary ml-2"
          >
            Open in New Tab
          </a>
        </div>
      </div>
    </div>

    <!-- Penalties Modal -->
    <div 
      v-if="selectedPenalties"
      class="penalties-modal-overlay" 
      @click="closePenaltiesModal"
    >
      <div class="penalties-modal-content" @click.stop>
        <div class="penalties-modal-header">
          <h5 class="modal-title">
            <i class="fa fa-exclamation-triangle text-danger"></i>
            Category Penalties & Warnings
          </h5>
          <button 
            type="button" 
            class="close" 
            @click="closePenaltiesModal"
            aria-label="Close"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="penalties-modal-body">
          <div class="penalties-list">
            <div 
              v-for="penalty in selectedPenalties" 
              :key="penalty.employeeId + penalty.actionDate"
              class="penalty-card mb-3"
            >
              <div class="penalty-header">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 class="mb-1">
                      <strong>{{ penalty.employeeName }}</strong>
                    </h6>
                    <small class="text-muted">
                      <i class="fa fa-calendar"></i>
                      {{ formatDate(penalty.actionDate) }} at {{ penalty.actionTime }}
                    </small>
                  </div>
                  <div class="penalty-badges">
                    <span 
                      class="badge mr-2"
                      :class="getActionBadgeClass(penalty.action)"
                    >
                      {{ penalty.action?.toUpperCase() || 'N/A' }}
                    </span>
                    <span 
                      v-if="penalty.penaltyAmount > 0"
                      class="badge badge-danger"
                    >
                      ₹{{ penalty.penaltyAmount }}
                    </span>
                  </div>
                </div>
              </div>
              
              <div class="penalty-content mt-3">
                <!-- Slip Image -->
                <div v-if="penalty.slip" class="penalty-media mb-3">
                  <h6 class="media-title">
                    <i class="fa fa-file-text text-primary"></i>
                    Penalty Slip
                  </h6>
                  <div class="media-gallery">
                    <div class="media-item">
                      <img 
                        :src="penalty.slip" 
                        :alt="`Penalty slip for ${penalty.employeeName}`"
                        class="penalty-slip-image"
                        @click="openImageModal(penalty.slip, `Penalty Slip - ${penalty.employeeName} - ${penalty.action}`)"
                      />
                      <div class="media-overlay">
                        <i class="fa fa-search-plus"></i>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Upload Photo -->
                <div v-if="penalty.uploadPhoto" class="penalty-media mb-3">
                  <h6 class="media-title">
                    <i class="fa fa-camera text-primary"></i>
                    Evidence Photo
                  </h6>
                  <div class="media-gallery">
                    <div class="media-item">
                      <img 
                        :src="penalty.uploadPhoto" 
                        :alt="`Penalty photo for ${penalty.employeeName}`"
                        class="penalty-photo-image"
                        @click="openImageModal(penalty.uploadPhoto, `Penalty Photo - ${penalty.employeeName} - ${penalty.action}`)"
                      />
                      <div class="media-overlay">
                        <i class="fa fa-search-plus"></i>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Additional Photos -->
                <div v-if="penalty.photos && penalty.photos.length > 0" class="penalty-media mb-3">
                  <h6 class="media-title">
                    <i class="fa fa-images text-primary"></i>
                    Additional Photos ({{ penalty.photos.length }})
                  </h6>
                  <div class="media-gallery">
                    <div 
                      v-for="(photo, photoIndex) in penalty.photos" 
                      :key="photoIndex"
                      class="media-item"
                    >
                      <img 
                        :src="photo" 
                        :alt="`Additional penalty photo ${photoIndex + 1}`"
                        class="penalty-photo-image"
                        @click="openImageModal(photo, `Additional Photo ${photoIndex + 1} - ${penalty.employeeName}`)"
                      />
                      <div class="media-overlay">
                        <i class="fa fa-search-plus"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="penalties-modal-footer">
          <button 
            type="button" 
            class="btn btn-secondary" 
            @click="closePenaltiesModal"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'

// Type definitions
interface Employee {
  employeeId: string
  employeeName: string
  employeeCode: string
}

interface Site {
  siteId: string
  siteName: string
  siteAddress: string
  siteUID: string
}

interface Assignment {
  _id: string
  assignmentId: string
  subCategoryLogNo: string
  parentLogNo: string
  kytOperationId: string
  employee: Employee
  employeeName: string
  employeeCode: string
  subCategory: {
    subCategoryId: string
    name: string
    description: string
  }
  assignedDate: string
  penalty: number
  priority: string
  photos: any[]
  isCompletedWork: boolean
  workStarted: boolean
  workCompletedAt?: string
  isVerified: boolean
  isSubmit: boolean
  requiresRework: boolean
  workNotes?: string
  statusHistory: any[]
  lastUpdated: string
  cascadeInfo: {
    originalLogNo: string
    totalOriginalAssignees: number
    completedInPreviousKyts: number
    remainingAssignees: number
    cascadeGeneration: number
  }
  createdAt: string
  updatedAt: string
}

interface PenaltyWarning {
  employeeId: string
  employeeName: string
  action: string
  penalty: number
  penaltyAmount: number
  slip: string
  actionDate: string
  actionTime: string
  isCompletedWork: boolean
  workStarted: boolean
  isVerified: boolean
  isSubmit: boolean
  uploadPhoto: string
  photos: any[]
  assignmentId?: string
}

interface CategoryData {
  category: string
  subCategory: string
  problem: string
  solution: string
  logNo: string
  status: string
  completionPercentage: number
  totalAssignments: number
  completedAssignments: number
  assignedPenaltiesWarnings?: PenaltyWarning[]
  employeeAssignments: Assignment[]
}

interface KytPenaltiesWarnings {
  totalActions: number
  warnings: number
  penalties: number
  totalPenaltyAmount: number
  actionsList: {
    category: string
    subCategory: string
    employeeName: string
    action: string
    penalty: number
    penaltyAmount: number
    slip: string
    actionDate: string
    actionTime: string
    logNo: string
  }[]
  actionsSummary: {
    [key: string]: number
  }
}

interface ChildKyt {
  _id: string
  mainLogNo: string
  siteName: string
  nfd: string
  previousNfd?: string | null
  status: string
  kytStatus: string
  createdAt: string
  employee: Employee
  kytPenaltiesWarnings?: KytPenaltiesWarnings | null
  employeePenaltyWarnings?: PenaltyWarning[] | null
  site: Site
  categoryData: CategoryData[]
}

interface Pagination {
  currentPage: number
  totalPages: number
  totalKyts: number
  hasNext: boolean
  hasPrev: boolean
  limit: number
  skip: number
}

interface ParentKyt {
  _id: string
  mainLogNo: string
  parentMainLogNo: string
  siteName: string
  nfd: string
  status: string
  kytStatus: string
  createdAt: string
  employee: Employee
  site: Site
  photos?: string[]
  totalPhotos?: number
}

interface KytData {
  parentMainLogNo: string
  totalChildKyts: number
  parentKyt?: ParentKyt
  childKyts: ChildKyt[]
  pagination: Pagination
}

// Reactive data
const kytData = ref<KytData | null>(null)
const loading = ref(false)
const error = ref('')
const searchTerm = ref('')
const statusFilter = ref('')
const priorityFilter = ref('')
const selectedImage = ref('')
const selectedImageTitle = ref('')
const selectedPenalties = ref<PenaltyWarning[] | null>(null)
const route = useRoute()

// Sample data based on your API response
const sampleData = {
  "success": true,
  "message": "Child KYTs with logNo retrieved successfully",
  "data": {
    "parentMainLogNo": "MLYHLWTXN1",
    "totalChildKyts": 2,
    "childKyts": [
      {
        "_id": "68c3e9faefce898880bb7d0d",
        "mainLogNo": "MLYHLWTXN1",
        "siteName": "Agil Glass Industries Pvt Ltd Plot No125A",
        "nfd": "2025-09-16",
        "status": "open",
        "createdAt": "2025-09-12T09:38:02.667Z",
        "employee": {
          "employeeId": "677ce932cf46ce38e9f43c28",
          "employeeName": "Test Employee",
          "employeeCode": "OPE01"
        },
        "site": {
          "siteId": "67a6f25a602f7c3ecd3ad1b7",
          "siteName": "Agil Glass Industries Pvt Ltd Plot No125A",
          "siteAddress": "Sed aut ad sunt volu",
          "siteUID": "SMS-0002"
        },
        "categoryData": [
          {
            "category": "manpower",
            "subCategory": "testing manpower 2",
            "problem": "the problem of testing manpower 2",
            "solution": "new solution",
            "logNo": "LDGAU4YQ",
            "status": "pending",
            "completionPercentage": 0,
            "totalAssignments": 2,
            "completedAssignments": 0,
            "employeeAssignments": [
              {
                "_id": "68c3e9fbefce898880bb7d1d",
                "assignmentId": "ASSIGN-20250912-0122",
                "subCategoryLogNo": "SUBCAT-20250912-0122",
                "parentLogNo": "LDGAU4YQ",
                "kytOperationId": "68c3e9faefce898880bb7d0d",
                "employee": {
                  "employeeId": "67ab4d0acffb60970f45bb84",
                  "employeeName": "Rahul Chakraborty",
                  "employeeCode": "MKT04"
                },
                "employeeName": "Rahul Chakraborty",
                "employeeCode": "MKT04",
                "subCategory": {
                  "subCategoryId": "6768677b2acfac15485c6e3a",
                  "name": "testing manpower 2",
                  "description": " "
                },
                "assignedDate": "2025-09-12T09:38:03.254Z",
                "penalty": 0,
                "priority": "medium",
                "photos": [],
                "isCompletedWork": false,
                "workStarted": false,
                "isVerified": false,
                "isSubmit": false,
                "requiresRework": false,
                "statusHistory": [],
                "lastUpdated": "2025-09-12T09:38:03.254Z",
                "cascadeInfo": {
                  "originalLogNo": "LDGAU4YQ",
                  "totalOriginalAssignees": 2,
                  "completedInPreviousKyts": 0,
                  "remainingAssignees": 2,
                  "cascadeGeneration": 1
                },
                "createdAt": "2025-09-12T09:38:03.255Z",
                "updatedAt": "2025-09-12T09:38:03.255Z"
              },
              {
                "_id": "68c3e9fbefce898880bb7d20",
                "assignmentId": "ASSIGN-20250912-0123",
                "subCategoryLogNo": "SUBCAT-20250912-0122",
                "parentLogNo": "LDGAU4YQ",
                "kytOperationId": "68c3e9faefce898880bb7d0d",
                "employee": {
                  "employeeId": "679b57edb51d4a3e357a1274",
                  "employeeName": "Test Employee 2",
                  "employeeCode": "EMP02"
                },
                "employeeName": "Test Employee 2",
                "employeeCode": "EMP02",
                "subCategory": {
                  "subCategoryId": "6768677b2acfac15485c6e3a",
                  "name": "testing manpower 2",
                  "description": " "
                },
                "assignedDate": "2025-09-12T09:38:03.376Z",
                "penalty": 0,
                "priority": "medium",
                "photos": [],
                "isCompletedWork": false,
                "workStarted": false,
                "isVerified": false,
                "isSubmit": false,
                "requiresRework": false,
                "statusHistory": [],
                "lastUpdated": "2025-09-12T09:38:03.376Z",
                "cascadeInfo": {
                  "originalLogNo": "LDGAU4YQ",
                  "totalOriginalAssignees": 2,
                  "completedInPreviousKyts": 0,
                  "remainingAssignees": 2,
                  "cascadeGeneration": 1
                },
                "createdAt": "2025-09-12T09:38:03.376Z",
                "updatedAt": "2025-09-12T09:38:03.376Z"
              }
            ]
          }
        ]
      },
      {
        "_id": "68c3ebfef12a1262e740135a",
        "mainLogNo": "MLYHLWTXN1",
        "siteName": "Agil Glass Industries Pvt Ltd Plot No125A",
        "nfd": "2025-09-19",
        "status": "open",
        "createdAt": "2025-09-12T09:46:38.680Z",
        "employee": {
          "employeeId": "677ce932cf46ce38e9f43c28",
          "employeeName": "Test Employee",
          "employeeCode": "OPE01"
        },
        "site": {
          "siteId": "67a6f25a602f7c3ecd3ad1b7",
          "siteName": "Agil Glass Industries Pvt Ltd Plot No125A",
          "siteAddress": "Sed aut ad sunt volu",
          "siteUID": "SMS-0002"
        },
        "categoryData": [
          {
            "category": "manpower",
            "subCategory": "testing manpower 2",
            "problem": "the problem of testing manpower 2",
            "solution": "this is the main work solutvbbion",
            "logNo": "TTW4KZRR",
            "status": "pending",
            "completionPercentage": 0,
            "totalAssignments": 1,
            "completedAssignments": 1,
            "employeeAssignments": [
              {
                "_id": "68c3ebfff12a1262e740136d",
                "assignmentId": "ASSIGN-20250912-0124",
                "subCategoryLogNo": "SUBCAT-20250912-0123",
                "parentLogNo": "TTW4KZRR",
                "kytOperationId": "68c3ebfef12a1262e740135a",
                "employee": {
                  "employeeId": "679b57edb51d4a3e357a1274",
                  "employeeName": "Test Employee 2",
                  "employeeCode": "EMP02"
                },
                "employeeName": "Test Employee 2",
                "employeeCode": "EMP02",
                "subCategory": {
                  "subCategoryId": "6768677b2acfac15485c6e3a",
                  "name": "testing manpower 2",
                  "description": " "
                },
                "assignedDate": "2025-09-12T09:46:39.444Z",
                "penalty": 0,
                "priority": "medium",
                "photos": [],
                "isCompletedWork": true,
                "workStarted": false,
                "workCompletedAt": "2025-09-12T09:46:52.541Z",
                "isVerified": false,
                "isSubmit": false,
                "requiresRework": false,
                "workNotes": "xbxbxbx",
                "statusHistory": [
                  {
                    "status": "work-completed",
                    "changedAt": "2025-09-12T09:46:52.183Z",
                    "reason": "Work marked as completed",
                    "_id": "68c3ec0cf12a1262e74013a7"
                  }
                ],
                "lastUpdated": "2025-09-12T09:46:52.183Z",
                "cascadeInfo": {
                  "originalLogNo": "TTW4KZRR",
                  "totalOriginalAssignees": 1,
                  "completedInPreviousKyts": 0,
                  "remainingAssignees": 1,
                  "cascadeGeneration": 1
                },
                "createdAt": "2025-09-12T09:46:39.446Z",
                "updatedAt": "2025-09-12T09:46:52.185Z"
              }
            ]
          }
        ]
      }
    ],
    "pagination": {
      "currentPage": 1,
      "totalPages": 1,
      "totalKyts": 2,
      "hasNext": false,
      "hasPrev": false,
      "limit": 50,
      "skip": 0
    }
  }
}

// Computed properties
const sortedKyts = computed(() => {
  if (!kytData.value?.childKyts) return []
  
  let filtered = [...kytData.value.childKyts]
  
  // Filter by search term
  if (searchTerm.value) {
    const search = searchTerm.value.toLowerCase()
    filtered = filtered.filter(kyt => 
      kyt.siteName.toLowerCase().includes(search) ||
      kyt.employee.employeeName.toLowerCase().includes(search) ||
      kyt.categoryData.some(cat => 
        cat.category.toLowerCase().includes(search) ||
        cat.subCategory.toLowerCase().includes(search)
      )
    )
  }
  
  // Filter by status
  if (statusFilter.value) {
    filtered = filtered.filter(kyt => kyt.kytStatus === statusFilter.value)
  }
  
  // Filter by priority (check assignments)
  if (priorityFilter.value) {
    filtered = filtered.filter(kyt => 
      kyt.categoryData.some(cat =>
        cat.employeeAssignments.some(assignment =>
          assignment.priority === priorityFilter.value
        )
      )
    )
  }
  
  // Sort by creation date, latest first
  return filtered.sort((a, b) => {
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  })
})

// Methods
const fetchKytData = async (logNo: string) => {
  loading.value = true
  error.value = ''
  
  try {
    const response = await fetch(`http://localhost:5020/api/main-logs/parent/${logNo}/kyts-with-logno`)
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    
    const result = await response.json()
    
    if (result.success) {
      kytData.value = result.data
    } else {
      throw new Error(result.message || 'Failed to fetch data')
    }
    
  } catch (err: any) {
    error.value = err.message || 'Failed to fetch KYT data. Please try again.'
    console.error('Error fetching KYT data:', err)
  } finally {
    loading.value = false
  }
}

const getStatusBadgeClass = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'completed':
      return 'badge-success'
    case 'approved':
      return 'badge-warning' // Changed to warning for pending display
    case 'closed':
      return 'badge-success'
    case 'open':
      return 'badge-primary'
    case 'pending':
      return 'badge-warning'
    case 'in-progress':
    case 'started':
      return 'badge-info'
    case 'cancelled':
    case 'rejected':
      return 'badge-danger'
    default:
      return 'badge-secondary'
  }
}

const getDisplayStatus = (status: string) => {
  if (status?.toLowerCase() === 'approved') {
    return 'PENDING'
  }
  return status?.toUpperCase()
}

const getActionBadgeClass = (action: string) => {
  switch (action?.toLowerCase()) {
    case 'penalty':
      return 'badge-danger'
    case 'warning':
      return 'badge-warning'
    case 'change':
      return 'badge-info'
    case 'note':
      return 'badge-secondary'
    default:
      return 'badge-light'
  }
}

const getPriorityBadgeClass = (priority: string) => {
  switch (priority?.toLowerCase()) {
    case 'high':
      return 'badge-danger'
    case 'medium':
      return 'badge-warning'
    case 'low':
      return 'badge-info'
    default:
      return 'badge-secondary'
  }
}

const getSubCategoryName = (category: CategoryData, kyt: ChildKyt) => {
  // First try to get from category.subCategory if it's a string name
  if (typeof category.subCategory === 'string' && !category.subCategory.match(/^[0-9a-fA-F]{24}$/)) {
    return category.subCategory
  }
  
  // Try to get from employee assignments
  const firstAssignment = category.employeeAssignments?.[0]
  if (firstAssignment?.subCategory?.name) {
    return firstAssignment.subCategory.name
  }
  
  // Fallback to the original subCategory value
  return category.subCategory || 'Unknown'
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString()
}

const formatDateTime = (dateString: string) => {
  return new Date(dateString).toLocaleString()
}

const getCompletedKyts = () => {
  if (!kytData.value?.childKyts) return 0
  
  // Count categories that are completed or closed
  let completedCount = 0
  kytData.value.childKyts.forEach(kyt => {
    kyt.categoryData.forEach(category => {
      if (category.status === 'completed' || category.status === 'closed' || category.completionPercentage === 100) {
        completedCount++
      }
    })
  })
  return completedCount
}

const getPendingKyts = () => {
  if (!kytData.value?.childKyts) return 0
  
  // Count categories that are not completed
  let pendingCount = 0
  kytData.value.childKyts.forEach(kyt => {
    kyt.categoryData.forEach(category => {
      if (category.status !== 'completed' && category.status !== 'closed' && category.completionPercentage < 100) {
        pendingCount++
      }
    })
  })
  return pendingCount
}

const getTotalCategories = () => {
  if (!kytData.value?.childKyts) return 0
  
  let totalCount = 0
  kytData.value.childKyts.forEach(kyt => {
    totalCount += kyt.categoryData.length
  })
  return totalCount
}

const getTotalPenaltyAmount = (penalties: PenaltyWarning[]) => {
  if (!penalties || penalties.length === 0) return 0
  
  return penalties.reduce((total, penalty) => {
    return total + (penalty.penaltyAmount || 0)
  }, 0)
}

const changePage = (page: number) => {
  // Implement pagination logic here
  console.log('Change to page:', page)
}

const refreshData = () => {
  const logNo = Array.isArray(route.params.logNo) ? route.params.logNo[0] : route.params.logNo || 'MLYHLWTXN1'
  fetchKytData(logNo)
}

// Image modal methods
const openImageModal = (imageUrl: string, title: string) => {
  selectedImage.value = imageUrl
  selectedImageTitle.value = title
  // Prevent body scrolling when modal is open
  document.body.style.overflow = 'hidden'
}

const closeImageModal = () => {
  selectedImage.value = ''
  selectedImageTitle.value = ''
  // Restore body scrolling
  document.body.style.overflow = 'auto'
}

// Penalties modal methods
const openPenaltiesModal = (category: CategoryData) => {
  selectedPenalties.value = category.assignedPenaltiesWarnings || null
  // Prevent body scrolling when modal is open
  document.body.style.overflow = 'hidden'
}

const closePenaltiesModal = () => {
  selectedPenalties.value = null
  // Restore body scrolling
  document.body.style.overflow = 'auto'
}

// Lifecycle
onMounted(() => {
  // Get log number from route params or use default
  const logNo = Array.isArray(route.params.logNo) ? route.params.logNo[0] : route.params.logNo || 'MLYHLWTXN1'
  fetchKytData(logNo)
})
</script>

<style scoped>
.kyts-container {
  max-width: 100%;
}

/* Table Styles */
.table {
  font-size: 0.9rem;
}

.table th {
  background-color: #343a40;
  color: white;
  font-weight: 600;
  border-top: none;
  font-size: 0.85rem;
  padding: 12px 8px;
  vertical-align: middle;
}

.table td {
  padding: 10px 8px;
  vertical-align: top;
  border-top: 1px solid #dee2e6;
}

.kyt-row:hover {
  background-color: #f8f9fa;
}

.employee-info strong {
  font-size: 0.9rem;
  color: #495057;
}

.category-info {
  line-height: 1.4;
}

.category-info .badge-sm {
  font-size: 0.7rem;
  padding: 0.25em 0.5em;
}

.assignments-compact {
  max-width: 200px;
}

.assignment-row {
  background-color: #f8f9fa;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 0.8rem;
}

.assignment-employee strong {
  font-size: 0.8rem;
}

.assignment-badges .badge {
  font-size: 0.65rem;
  padding: 0.2em 0.4em;
}

.completion-time {
  background-color: #d4edda;
  border-radius: 3px;
  padding: 2px 6px;
  margin-top: 4px;
}

.completion-time .fa {
  margin-right: 3px;
}

.work-notes {
  background-color: #d1ecf1;
  border-radius: 3px;
  padding: 2px 6px;
  margin-top: 4px;
}

.work-notes .fa {
  margin-right: 3px;
}

.text-success {
  color: #28a745 !important;
}

.text-info {
  color: #17a2b8 !important;
}

.badge {
  font-size: 0.75rem;
}

.badge-xs {
  font-size: 0.6rem;
  padding: 0.2em 0.4em;
}

.badge-sm {
  font-size: 0.7rem;
  padding: 0.25em 0.5em;
}

.progress {
  background-color: #e9ecef;
  height: 12px;
}

.progress-bar {
  font-size: 0.7rem;
  line-height: 12px;
}

.text-muted {
  color: #6c757d !important;
}

.small {
  font-size: 0.8rem;
}

/* Responsive table */
@media (max-width: 768px) {
  .table-responsive {
    font-size: 0.8rem;
  }
  
  .table th,
  .table td {
    padding: 8px 4px;
  }
  
  .assignments-compact {
    max-width: 150px;
  }
}

/* Summary cards */
.card {
  border: none;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  margin-bottom: 1rem;
}

.card-body {
  padding: 1rem;
}

/* Status badges */
.badge-success {
  background-color: #28a745;
}

.badge-warning {
  background-color: #ffc107;
  color: #212529;
}

.badge-danger {
  background-color: #dc3545;
}

.badge-info {
  background-color: #17a2b8;
}

.badge-primary {
  background-color: #007bff;
}

.badge-secondary {
  background-color: #6c757d;
}

.status-info {
  text-align: center;
  line-height: 1.3;
}

.status-info .badge {
  display: inline-block;
  min-width: 60px;
}

/* Penalties & Warnings Styles */
.penalties-warnings-compact {
  max-width: 200px;
}

.penalties-overview {
  max-width: 180px;
  font-size: 0.8rem;
}

.penalties-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 0.2rem;
  justify-content: center;
}

.penalties-brief {
  line-height: 1.2;
}

.penalty-brief-item {
  background-color: #f8f9fa;
  border-radius: 3px;
  padding: 2px 6px;
  margin-bottom: 2px;
  border-left: 2px solid #dc3545;
}

.penalties-header {
  margin-bottom: 0.5rem;
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 2px;
}

.penalty-row {
  background-color: #fff3cd;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 0.8rem;
  border-left: 3px solid #ffc107;
}

.penalty-row:last-child {
  margin-bottom: 0 !important;
}

.penalty-info {
  line-height: 1.2;
}

.penalty-badges .badge {
  font-size: 0.65rem;
  padding: 0.2em 0.4em;
}

.penalty-details {
  background-color: rgba(255, 255, 255, 0.5);
  border-radius: 3px;
  padding: 2px 6px;
}

.penalty-details .fa {
  margin-right: 3px;
}

.penalty-slip {
  background-color: #d1ecf1;
  border-radius: 3px;
  padding: 2px 6px;
  margin-top: 4px;
}

.penalty-slip .fa {
  margin-right: 3px;
}

/* Image Gallery Styles */
.assignment-photos, .penalty-photos, .penalty-photo {
  background-color: rgba(255, 255, 255, 0.8);
  border-radius: 4px;
  padding: 4px;
  margin-top: 4px;
}

.photos-header {
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 2px;
}

.photo-thumbnail {
  position: relative;
  display: inline-block;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.photo-thumbnail:hover {
  transform: scale(1.05);
}

.photo-thumbnail img {
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 4px;
  border: 2px solid #dee2e6;
  transition: border-color 0.2s ease;
}

.photo-thumbnail:hover img {
  border-color: #007bff;
}

.photos-gallery {
  gap: 4px;
}

/* Image Modal Styles */
.image-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  backdrop-filter: blur(2px);
}

.image-modal-content {
  background: white;
  border-radius: 8px;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.image-modal-header {
  padding: 1rem;
  border-bottom: 1px solid #dee2e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #f8f9fa;
}

.image-modal-header .modal-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #495057;
}

.image-modal-header .close {
  background: none;
  border: none;
  font-size: 1.5rem;
  font-weight: bold;
  color: #aaa;
  cursor: pointer;
  padding: 0;
  line-height: 1;
}

.image-modal-header .close:hover {
  color: #000;
}

.image-modal-body {
  padding: 1rem;
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
  overflow: hidden;
}

.modal-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 4px;
}

.image-modal-footer {
  padding: 1rem;
  border-top: 1px solid #dee2e6;
  display: flex;
  justify-content: flex-end;
  background-color: #f8f9fa;
}

.image-modal-footer .btn {
  border-radius: 4px;
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .image-modal-content {
    max-width: 95vw;
    max-height: 95vh;
  }
  
  .image-modal-header,
  .image-modal-footer {
    padding: 0.75rem;
  }
  
  .image-modal-body {
    padding: 0.75rem;
  }
  
  .photo-thumbnail img {
    width: 30px;
    height: 30px;
  }
}

/* Penalties Modal Styles */
.penalties-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  backdrop-filter: blur(2px);
}

.penalties-modal-content {
  background: white;
  border-radius: 8px;
  max-width: 80vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  width: 100%;
}

.penalties-modal-header {
  padding: 1rem;
  border-bottom: 1px solid #dee2e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #f8f9fa;
}

.penalties-modal-header .modal-title {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 600;
  color: #495057;
}

.penalties-modal-header .close {
  background: none;
  border: none;
  font-size: 1.5rem;
  font-weight: bold;
  color: #aaa;
  cursor: pointer;
  padding: 0;
  line-height: 1;
}

.penalties-modal-header .close:hover {
  color: #000;
}

.penalties-modal-body {
  padding: 1rem;
  flex: 1;
  overflow-y: auto;
}

.penalties-modal-footer {
  padding: 1rem;
  border-top: 1px solid #dee2e6;
  display: flex;
  justify-content: flex-end;
  background-color: #f8f9fa;
}

.penalty-card {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
  border-left: 4px solid #dc3545;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.penalty-header h6 {
  color: #495057;
  font-weight: 600;
}

.penalty-badges .badge {
  font-size: 0.8rem;
  padding: 0.4em 0.8em;
}

.penalty-content {
  border-top: 1px solid #dee2e6;
  padding-top: 1rem;
}

.penalty-media {
  margin-bottom: 1rem;
}

.media-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.media-gallery {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.media-item {
  position: relative;
  cursor: pointer;
  border-radius: 8px;
  overflow: hidden;
  transition: transform 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.media-item:hover {
  transform: scale(1.05);
}

.penalty-slip-image,
.penalty-photo-image {
  width: 120px;
  height: 120px;
  object-fit: cover;
  border-radius: 8px;
  display: block;
}

.media-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transition: opacity 0.2s ease;
  color: white;
  font-size: 1.2rem;
}

.media-item:hover .media-overlay {
  opacity: 1;
}

/* Responsive penalties modal */
@media (max-width: 768px) {
  .penalties-modal-content {
    max-width: 95vw;
    max-height: 95vh;
  }
  
  .penalties-modal-header,
  .penalties-modal-footer {
    padding: 0.75rem;
  }
  
  .penalties-modal-body {
    padding: 0.75rem;
  }
  
  .penalty-slip-image,
  .penalty-photo-image {
    width: 80px;
    height: 80px;
  }
  
  .penalty-card {
    padding: 0.75rem;
  }
}

/* Parent Photos Gallery Styles */
.parent-photos-section .card-header {
  background-color: #17a2b8 !important;
}

.parent-photos-section .card-header h6 {
  font-weight: 600;
}

.parent-photos-gallery {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  padding: 0.5rem 0;
}

.parent-photo-item {
  position: relative;
  cursor: pointer;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  width: 150px;
  height: 150px;
}

.parent-photo-item:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
}

.parent-photo-thumbnail {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.parent-photo-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  color: white;
  font-size: 1.5rem;
}

.parent-photo-item:hover .parent-photo-overlay {
  opacity: 1;
}

.parent-photo-number {
  position: absolute;
  top: 8px;
  right: 8px;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 0.9rem;
  font-weight: 600;
  z-index: 1;
}

/* Responsive parent photos */
@media (max-width: 768px) {
  .parent-photos-gallery {
    gap: 0.5rem;
  }
  
  .parent-photo-item {
    width: 100px;
    height: 100px;
  }
  
  .parent-photo-number {
    width: 24px;
    height: 24px;
    font-size: 0.75rem;
  }
  
  .parent-photo-overlay {
    font-size: 1.2rem;
  }
}

@media (max-width: 480px) {
  .parent-photo-item {
    width: 80px;
    height: 80px;
  }
}
</style>